import clsx from 'clsx';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import IconArrow from '@components/Images/IconArrow';
import LazyImgixImage from '@components/LazyImgixImage';
import useEventTracking from '@hooks/useEventTracking';
import { event_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { Event } from '@utils/backend-api/events';

import {
  DESCRIPTION_MAX_LENGTH,
  MORE_INFO_TEXT,
  SECURE_YOUR_SPOT_TEXT,
  SPONSOR_TEXT,
} from './constant';

interface FutureEventCardProps {
  event: Event;
}

function toPlainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const FutureEventCard = ({ event }: FutureEventCardProps) => {
  const description = event.description ?? '';
  const plain = toPlainText(description);
  const isTruncated = plain.length > DESCRIPTION_MAX_LENGTH;
  const { trackEvent } = useEventTracking();
  return (
    <div className={'flex shrink-0 flex-wrap gap-36 md:flex-nowrap'}>
      <LazyImgixImage
        src={event.pictureUrl}
        alt={event.name}
        className="w-full"
        sizes="(min-width: 1200px) 500px, (min-width: 768px) 300px, (min-width: 480px) 600px, 400px"
        wrapperClassName={clsx('aspect-[16/9] h-auto w-full object-cover shrink-0', {
          'max-w-full md:max-w-[571px]': event.isFeatured,
          'max-w-full sm:max-w-[300px] md:max-w-[413px]': !event.isFeatured,
        })}
      />
      <div className="flex max-w-full flex-col lg:max-w-[649px]">
        <h3
          className={clsx('m-0 font-suisse-works', {
            'text-headline-sm md:text-headline-md': event.isFeatured,
            'text-headline-xs': !event.isFeatured,
          })}
        >
          {event.name}
        </h3>
        {event.isFeatured && (
          <p className="m-0 py-16 font-suisse-neue text-body-lg">
            {isTruncated
              ? `${plain.slice(0, DESCRIPTION_MAX_LENGTH - 3)}…`
              : ReactHtmlParser(description)}
          </p>
        )}
        <p className="m-0 py-0 font-suisse-neue text-subheadline-sm text-grayscale-charcoal">
          {event.date} \ {event.location}
        </p>

        <div className="flex flex-wrap items-center gap-24 pt-16">
          <PrimaryButton
            as="link"
            className="mt-16 !w-fit rounded-40 !px-16 py-8 font-suisse-intl text-body-md font-medium sm:mt-24 md:mt-0"
            href={event_path(event.slug)}
            target="_blank"
            onClickCapture={() =>
              trackEvent(
                click({ clickTarget: ClickTarget.BuyTickets, clickText: SECURE_YOUR_SPOT_TEXT })
              )
            }
          >
            <div className="flex items-center gap-16">
              <span>{SECURE_YOUR_SPOT_TEXT}</span>
              <IconArrow className="h-16 w-16 rotate-180" />
            </div>
          </PrimaryButton>
          {event.isFeatured && (
            <>
              <TextButton
                as="link"
                className="mt-16 !w-fit rounded-40 font-suisse-intl text-body-md sm:mt-24 md:mt-0"
                href={`${event_path(event.slug)}#partners`}
                target="_blank"
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.Sponsor, clickText: SPONSOR_TEXT }))
                }
              >
                <span>{SPONSOR_TEXT}</span>
              </TextButton>
              <TextButton
                as="link"
                className="mt-16 !w-fit rounded-40 font-suisse-intl text-body-md sm:mt-24 md:mt-0"
                href={`${event_path(event.slug)}#agenda`}
                target="_blank"
                onClickCapture={() =>
                  trackEvent(
                    click({ clickTarget: ClickTarget.MoreInfo, clickText: MORE_INFO_TEXT })
                  )
                }
              >
                <span>{MORE_INFO_TEXT}</span>
              </TextButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FutureEventCard;
